import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import { AffiliationList, TableHeader, ColumnMenu, PullDown, ErrorTableType, ColumnSearch } from '@/shared/type';
import SystemConst from '@/shared/system-const';                                        // 定数定義
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import { DataOptions } from 'vuetify';                                                  // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';  // フィルタ用共通コンポーネント
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import { FilePondFile } from 'filepond';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import HttpStatus from 'http-status-codes';                                             // HTTPステータスコード取得用

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                       // Windowサイズ
    affiliationListItem: AffiliationList[];                                     // 所属コード変換テーブル一覧情報
    conversionAffiliationTableHeaders: TableHeader[];                           // 所属コード変換テーブルヘッダー情報
    total: number;                                                              // 全ページの件数
    isLoading: boolean;                                                         // テーブルがロード中: true ロード中でない: false
    pageOptions: DataOptions;                                                   // ページ情報・ソート情報
    filterDefs: TableFilterDefs;                                                // フィルタ定義
    filters: TableFilters;                                                      // フィルタ条件
    affiliationCodeSelectDialog: ColumnMenu;                                    // 変換情報の編集ダイアログ表示
    affiliationCodeDeleteDialog: ColumnMenu;                                    // 変換情報の削除ダイアログ表示
    affiliationCodeUploadDialog: boolean;                                       // ファイル取込ダイアログ表示
    isLoadingDialog: boolean;                                                   // ファイル取込中のローディングダイアログ表示
    isClear: boolean;                                                           // ファイル取込入力欄のクリア中判別
    selectData: ColumnSearch;                                                   // 編集ダイアログ設定値
    pulldown: PullDown[];                                                       // 編集ダイアログ選択プルダウンデータ
    searchIndividual: ColumnSearch;                                             // 編集ダイアログ検索情報
    validateValues: ValidateType;                                               // 取得したファイル取込バリデーションの値
    isFileValid: boolean;                                                       // ファイル取込のバリデーション結果（正常: true 異常: false）
    fileErrorMessage: string;                                                   // 所属コードファイル入力エラーメッセージ
    showErrorMessageDialog: boolean;                                            // ファイルバリデーションエラーダイアログ表示
    fileErrorItems: ErrorTableType[];                                           // ファイルバリデーションエラー内容
    isAllCheckMark: boolean;                                                    // 全データチェックマークアイコン表示判別
};

// バリデーション用パラメータ
interface ValidateType {
    [key: string]: string;                                                      // パラメータ値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

export default defineComponent({
    components: { CommonTableFilter, FilePond, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                   // 表示件数定義
        const commonRef = ref<any>(null);                                       // 共通ダイアログ
        const child = ref<any>(null);                                           // フィルタ条件エリア
        const form = ref<any>(null);                                            // 更新ダイアログ入力フォーム
        const pondRef = ref<any>(null);                                         // ファイルアップロードエリア
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                         // ウィンドウサイズ
            affiliationListItem: [],                                            // 所属コード変換テーブル一覧情報
            conversionAffiliationTableHeaders: [],                              // 所属コード変換テーブルヘッダー
            total: 0,                                                           // 全ページの件数
            isLoading: false,                                                   // テーブルがロード中: true ロード中でない: false
            pageOptions: {                                                      // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filterDefs: {                                                       // フィルタ定義
                'beforeAffiliationCode': { title: '(変換元)所属コード', types: 'KeyWordPartialMatch' },
                'beforeDepartmentName': { title: '部局名称', types: 'KeyWordPartialMatch' },
                'beforeAffiliationName': { title: '所属名称', types: 'KeyWordPartialMatch' },
                'beforeAffiliationName1': { title: '所属名称1', types: 'KeyWordPartialMatch' },
                'afterAffiliationCode': { title: '(変換先)所属コード', types: 'KeyWordPartialMatch' },
                'afterOrganizationName': { title: '組織名称', types: 'KeyWordPartialMatch' }
            },
            filters: {},                                                        // フィルタ情報
            affiliationCodeSelectDialog: {},                                    // 変換先更新ダイアログ表示
            affiliationCodeDeleteDialog: {},                                    // 削除ダイアログ表示
            affiliationCodeUploadDialog: false,                                 // ファイル取込ダイアログ表示
            isLoadingDialog: false,                                             // ファイル取込中のローディングダイアログ表示
            isClear: false,                                                     // 取込ファイル入力欄をシステムによるクリア判別
            selectData: {},                                                     // 変換先更新ダイアログ設定値
            pulldown: [],                                                       // 変換先更新ダイアログデータ
            searchIndividual: {},                                               // 変換先更新ダイアログプルダウン検索情報
            validateValues: {},                                                 // 取得したファイル取込バリデーションの値
            isFileValid: true,                                                  // ファイル取込のバリデーション結果
            fileErrorMessage: '',                                               // ファイル取込ダイアログエラーメッセージ
            showErrorMessageDialog: false,                                      // エラーメッセージダイアログ表示切り替え
            fileErrorItems: [],                                                 // ファイル内バリデーションエラーの表示内容
            isAllCheckMark: false                                               // 全データチェックマークアイコン表示判別
        });
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                // テーブルのデータ取得中に表示するテキスト
        const pageNation = ref<number>(1);                                      // ページネーション(vuex管理用)
        const itemsPerPage = ref<number>(linesPerPage[1]);                      // 表示件数(vuex管理用)
        const sortBy = ref<string[]>([]);                                       // ソートカラム
        const sortDesc = ref<boolean[]>([]);                                    // ソート昇順、降順
        let isPageLastData = false;                                             // ページの最終データフラグ
        const tableHeaderClass: string[] = ['secondary subtitle-2'];            // カテゴリ設定のヘッダー色
        const tableHeight = SystemConst.TABLE_HEIGHT.AFFILIATION_CODE_LIST;     // テーブル高さ調整用変数
        let errors: string[] = [];                                              // 所属コード変換ファイルのダイアログ表示用エラーメッセージ
        let pulldownValue = '';                                                 // プルダウン初期設定値格納
        let isExistErrorMessage = false;                                        // 登録押下時のエラーメッセージフラグ（false: テーブルデータなしエラー）
        // テーブルヘッダー（初期表示）
        const conversionAffiliationTableHeaders: TableHeader[] = [
            {
                text: '',
                value: 'icon',
                width: '60px',
                class: tableHeaderClass,
                sortable: false
            },
            {
                text: '(変換元)\n所属コード',
                value: 'beforeAffiliationCode',
                width: '170px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '\n部局名称',
                value: 'beforeDepartmentName',
                width: '150px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '\n所属名称',
                value: 'beforeAffiliationName',
                width: '150px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '\n所属名称1',
                value: 'beforeAffiliationName1',
                width: '160px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '(変換先)\n所属コード',
                value: 'afterAffiliationCode',
                width: '170px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '\n組織名称',
                value: 'afterOrganizationName',
                width: '160px',
                class: tableHeaderClass,
                sortable: true
            },
            {
                text: '',
                value: 'icon2',
                width: '3%',
                class: tableHeaderClass,
                sortable: false,
                align: 'end'
            }
        ];
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;          // フィルタに設定された最大桁数
        // (変換元)所属コード
        const beforeAffiliationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '(変換元)所属コード')
        ];
        // 部局名称
        const beforeDepartmentNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '部局名称')
        ];
        // 所属名称
        const beforeAffiliationNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '所属名称')
        ];
        // 所属名称1
        const beforeAffiliationName1Rules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '所属名称1')
        ];
        // (変換先)所属コード
        const afterAffiliationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '(変換先)所属コード')
        ];
        // 組織名称
        const afterOrganizationNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '組織名称')
        ];
        // 変換先組織の設定用プルダウン
        const updateOrganizationRules = [
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['変換先組織'])
        ];
        // 所属コード変換ファイルのバリデーション
        const filesRules = [
            () => {
                // バリデーションはAPIの結果を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // ファイルの情報を得る
                const files: FilePondFile[] = pondRef.value.getFiles();

                // ファイルが最低1つ登録されていることを確認
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }

                // 引数に以下を設定して、validateFiles()を呼び出し、バリデーションを設定する
                //   登録ファイル数、ファイルサイズ、ファイル拡張子、メッセージ
                return validateFiles(files,
                    Number(state.validateValues['fileCount']),
                    Number(state.validateValues['fileMaxSize']),
                    state.validateValues['fileExtension'],
                    '所属コード変換');
            }
        ];

        /**
         * ファイルバリデーション
         * 
         * @param files 対象ファイル
         * @param cnt バリデーション値（ファイル数）
         * @param size バリデーション値（ファイルサイズ）
         * @param ext バリデーション値（拡張子）
         * @param message メッセージ（表示するファイル名）
         * @returns 正常の場合true エラーの場合メッセージ文字列
         */
        function validateFiles(files: FilePondFile[], cnt: number, size: number, ext: string, message: string) {
            // ファイル数が超過していないか
            if (files.length > cnt) {
                return CommonFunction.replaceArray(
                    MessageConst.E_FILE_FILE_COUNT, [
                    message,
                    cnt + '件のファイル']);
            }

            // ファイルサイズが超過または0バイトでないか
            for (const key in files) {
                if (files[key].fileSize > size) {
                    // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_MAX_SIZE, [size / 1024]);
                } else if (files[key].fileSize === 0) {
                    // ファイルサイズが0バイトの場合、エラーメッセージを返す
                    return MessageConst.E_FILE_NO_DATA;
                }
            }

            // 拡張子が登録対象か
            for (const key in files) {
                // 登録しようとするファイルの拡張子を得る
                const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                const fileExtension = parts[1];

                // m_parametersの拡張子一覧
                const validateExtension = ext.split(',');

                if (validateExtension.indexOf(fileExtension) === -1) {
                    // 比較した結果、拡張子一覧に含まれていないとき、エラーメッセージを返す
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_EXTENSION, [ext]);
                }
            }

            return true;
        }

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-database-sync');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '所属コード変換');

            // 初期表示ヘッダー設定
            state.conversionAffiliationTableHeaders = conversionAffiliationTableHeaders;

            // フィルタの初期設定
            initFilter();

            // ファイル取込のバリデーション情報取得
            getFileValidation();

            // 組織選択プルダウンを取得する
            getPullDown();

            // 所属コード変換情報初期取得
            getConversionAffiliationCode(true);
        }

        /**
         * フィルタ初期設定
         */
        function initFilter() {
            // バリデーションを設定する
            // (変換元)所属コード
            state.filterDefs['beforeAffiliationCode'].validator = beforeAffiliationCodeRules;
            // 部局名称
            state.filterDefs['beforeDepartmentName'].validator = beforeDepartmentNameRules;
            // 所属名称
            state.filterDefs['beforeAffiliationName'].validator = beforeAffiliationNameRules;
            // 所属名称1
            state.filterDefs['beforeAffiliationName1'].validator = beforeAffiliationName1Rules;
            // (変換先)所属コード
            state.filterDefs['afterAffiliationCode'].validator = afterAffiliationCodeRules;
            // 組織名称
            state.filterDefs['afterOrganizationName'].validator = afterOrganizationNameRules;
        }

        /**
         * 所属コード変換ファイルのバリデーション情報を取得
         */
        async function getFileValidation() {
            // バリデーション取得APIを呼び出す(affiliationCodeConversion/getParameters)
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/getParameters');

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // バリデーション取得APIの結果を state.validateValues へ設定する
            state.validateValues['fileCount'] = response.data.fileCount;
            state.validateValues['fileMaxSize'] = response.data.fileMaxSize;
            state.validateValues['fileExtension'] = response.data.fileExtension;
        }

        /**
         * 変換先組織選択プルダウン取得
         */
        async function getPullDown() {
            // 変換先組織選択プルダウン取得APIを呼び出す
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/getOrganizationPullDown');

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得したプルダウン情報を state.pulldown に格納する
            state.pulldown = response.data.afterOrganizations;
        }

        /**
         * 所属コード変換情報取得
         *
         * @param isInitial 初期表示判別パラメータ（デフォルト値: false）
         */
        async function getConversionAffiliationCode(isInitial = false) {
            // ロード中のフラグをtrueにする
            state.isLoading = true;

            // ページの最終データフラグをfalseに設定する
            isPageLastData = false;

            // 所属コード変換テーブル内の情報をクリア
            state.affiliationListItem = [];
            state.total = 0;

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 所属コード変換情報取得APIを呼び出す
            // パラメータ：作成したパラメータ
            //             初期表示判別パラメータ
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/getConversionData',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    isInitial: isInitial
                }
            );

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // ロード中のフラグをfalseに設定する
                state.isLoading = false;

                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // テーブル情報を更新する
            state.affiliationListItem = response.data.conversionAffiliationCode;
            // 総データ件数の更新を行う
            state.total = response.data.totalCount;

            // データを取得できた場合、「登録」押下時のメッセージ変更
            if (response.data.totalCount > 0) {
                isExistErrorMessage = true;
            }

            // 全ての行にチェックマークがついているか判別するパラメータを取得する
            state.isAllCheckMark = response.data.isAllConversion;

            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.affiliationListItem.length === 1 && state.total > 1) {
                isPageLastData = true;
            }

            // ロード中のフラグをfalseにする
            state.isLoading = false;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 1ページへ戻す
            state.pageOptions.page = 1;
            pageNation.value = 1;

            // 所属コード変換情報取得
            getConversionAffiliationCode();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // データの中身を比較して、いずれも変更されていなかった場合、
                // ページの更新を行わない
                return;
            }

            // ページ情報を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソート情報を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 所属コード変換情報取得
            getConversionAffiliationCode();
        }

        /**
         * ファイル取込処理
         */
        async function importFile() {
            // updateAffiliationCodeConvertingFile()を呼びだし、バリデーションチェック
            updateAffiliationCodeConvertingFile();
            // バリデーションチェックを行い、エラーの場合
            if (!state.isFileValid) {
                // 以降の処理を行わない
                return;
            }

            // ダイアログを閉じる
            state.affiliationCodeUploadDialog = false;

            // APIへ渡すためにFilePondFileオブジェクトからFileオブジェクトを作る
            const files: File[]
                = pondRef.value.getFiles().map((e: FilePondFile) => e.file);

            // 取込中のローダーダイアログを表示する
            state.isLoadingDialog = true;

            // ファイル取込APIを呼び出す
            //   パラメータ：取込対象ファイル
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/importFileData',
                {
                    file: files
                }
            );

            // 取込中のローダーダイアログを閉じる
            state.isLoadingDialog = false;

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.BAD_REQUEST) {
                    // バリデーションチェックでエラーが発生した場合（400のステータスコード）
                    // レスポンスのエラー内容を errors に保存する
                    errors = response.data.errors;
                    // displayErrorDialog()を呼び出し、エラーダイアログを表示する
                    displayErrorDialog();
                    return;
                } else {
                    // 400以外のステータスコードのエラーの場合
                    // エラー画面へ遷移
                    commonRef.value.errorDisplayTransitionControl(response);
                    return;
                }
            }

            // ページ情報が1ページ目でない場合
            if (pageNation.value !== 1) {
                // 1ページ目へ戻す
                pageNation.value = 1;
                state.pageOptions.page = 1;
            }

            // フィルター条件が設定されていた場合
            if (Object.keys(state.filters).length !== 0) {
                // フィルター条件を解除する
                child.value.allClear();
            }

            // テーブルを更新するため、引数に値を設定せずに getConversionAffiliationCode()を呼び出す
            getConversionAffiliationCode();

            // pondRefの中身を初期化する
            pondRef.value.removeFiles();
            // システムによるクリアであることを設定する
            state.isClear = true;
        }

        /**
         * 所属コード変換ファイルが追加・削除された時のイベント
         */
        function updateAffiliationCodeConvertingFile() {
            // ファイルのバリデーションをチェックする
            for (let index = 0; index < filesRules.length; index++) {
                const rule: any = filesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // システムによるクリア中はバリデーションを実施しない
                    if (state.isClear) {
                        state.isClear = false;
                        break;
                    }
                    // エラーあり
                    // エラーメッセージ表示
                    state.fileErrorMessage = valid;
                    state.isFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.fileErrorMessage = '';
                    state.isFileValid = true;
                }
            }
        }

        /**
         * エラーダイアログメッセージ設定
         */
        function displayErrorDialog() {
            // ファイル内容のバリデーションエラー state.fileErrorItems を初期化する
            state.fileErrorItems = [];
            // エラーレスポンスデータの件数分繰り返す
            for (let i = 0; i < errors.length; i++) {
                // エラーメッセージ一件取得して「、」で分割する
                const oneError = errors[i].split('、');
                // 「、」で分割した最初のデータが数字の場合
                if (/^[0-9]+$/.test(oneError[0])) {
                    // 「、」で分割した最初のデータをstate.fileErrorItems の'errorLocation'に設定する
                    // 「、」で分割した後ろのデータをstate.fileErrorItems の'message'に設定する
                    state.fileErrorItems[i] = { errorLocation: oneError[0], message: oneError[1] };
                } else {
                    // 「、」で分割した最初のデータが数字でない、または分割できなかった場合
                    // state.fileErrorItems の'message'にエラーメッセージを格納する
                    state.fileErrorItems[i] = { errorLocation: '', message: oneError[0] };
                }
            }

            // エラーダイアログを表示する
            state.showErrorMessageDialog = true;
            return;
        }

        /**
         * 変換先組織更新ダイアログ初期表示
         * @param beforeAffiliationCode 変換元所属コード
         * @param organizationId 変換後組織ID
         */
        function displayUpdateDialog(beforeAffiliationCode: string, organizationId: string) {
            // ダイアログ表示
            ctx.root.$set(state.affiliationCodeSelectDialog, beforeAffiliationCode, true);
            pulldownValue = '';

            // state.pulldown.value に organizationId が一致するデータがあるか判別する
            const index = state.pulldown.findIndex(element => element.value === organizationId);
            // 一致するデータがある場合、一致したプルダウン情報を格納する
            if (index >= 0) {
                // 設定済みデータ表示
                pulldownValue = state.pulldown[index].value;
                state.selectData[beforeAffiliationCode] = state.pulldown[index].value;
            }
            return;
        }

        /**
         * 変換先組織更新ダイアログ削除
         * @param beforeAffiliationCode 変換元所属コード
         */
        function closeUpdateDialog(beforeAffiliationCode: string) {
            // ダイアログを閉じる
            ctx.root.$set(state.affiliationCodeSelectDialog, beforeAffiliationCode, false);

            // 設定済みデータ削除
            state.selectData[beforeAffiliationCode] = '';
            // バリデーションをリセットする
            form.value.resetValidation();
            return;
        }

        /**
         * 変換先組織更新
         * @param beforeAffiliationCode 変換元所属コード
         */
        async function updateAfterOrganization(beforeAffiliationCode: string) {
            if (!form.value.validate()) {
                // 設定されていなかった場合、処理をしない
                return;
            }

            // ダイアログを閉じる
            ctx.root.$set(state.affiliationCodeSelectDialog, beforeAffiliationCode, false);

            // 引数の変換元所属コードから設定された組織情報を取得する
            const selectOrgId = state.selectData[beforeAffiliationCode];

            // 設定された組織情報が変わらない状態で「設定」が押されたか判別
            if (pulldownValue === selectOrgId) {
                return;
            }

            // 設定されていた場合、変換先組織更新APIを呼び出す
            // パラメータ：beforeAffiliationCode
            //             プルダウンに設定した組織ID
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/updateOrganization',
                {
                    beforeAffiliationCode,
                    selectOrgId: selectOrgId
                });

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 正常に更新できた場合、所属コード変換情報取得
            getConversionAffiliationCode();
        }

        /**
         * 所属コード変換情報削除
         * @param beforeAffiliationCode 変換元所属コード
         */
        async function deleteConversionData(beforeAffiliationCode: string) {
            // 所属コード変換情報削除APIを呼び出す
            // パラメータ：beforeAffiliationCode
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/deleteConversionData',
            {
                beforeAffiliationCode
            });

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // ページの最終データフラグがtrueの場合、ページ情報を-1する
            if (isPageLastData) {
                state.pageOptions.page -= 1;
                pageNation.value -= 1;
            }

            // 正常に削除できた場合、所属コード変換情報取得
            getConversionAffiliationCode();
        }

        /**
         * 所属コード変換「登録」押下時処理
         */
        async function displayAddDialog() {
            // テーブル情報が格納されていることを確認する
            if (!isExistErrorMessage) {
                // テーブルにデータがない場合、エラーメッセージを表示
                commonRef.value.simpleDisplayMessageDialog(MessageConst.E_EXIST_AFFILIATION_CODE_CONVERSION);
                return;
            }

            // 全ての行にチェックマークがついているか判別する
            if (!state.isAllCheckMark) {
                // チェックマークがついていない場合、エラーメッセージを表示
                commonRef.value.simpleDisplayMessageDialog(MessageConst.E_SETTING_AFTER_ORGANIZATION);
                return;
            }

            // 登録確認ダイアログを表示する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['所属コード変換登録']);
            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 登録確認ダイアログの「はい」を押下時の処理
         */
        async function processingData() {
            // 所属コード変換処理を行うためのAPIを呼び出す
            const response = await ctx.root.$store.dispatch('affiliationCodeConversion/addConversionData');

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合、エラーダイアログを表示、またはエラー画面へ遷移
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 登録完了ダイアログを表示する
            commonRef.value.displayMessageDialog(response);

            // ページ情報が1ページ目でない場合
            if (pageNation.value !== 1) {
                // 1ページ目へ戻す
                pageNation.value = 1;
                state.pageOptions.page = 1;
            }

            // 所属コード変換情報を再表示するため、getConversionAffiliationCode()を呼びだす
            getConversionAffiliationCode(true);
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            commonRef,
            child,
            pondRef,
            form,
            state,
            pageNation,
            itemsPerPage,
            linesPerPage,
            tableHeight,
            tableLoadingText,
            updateOrganizationRules,
            updateFilter,
            updateOptions,
            updateAffiliationCodeConvertingFile,
            importFile,
            displayUpdateDialog,
            closeUpdateDialog,
            updateAfterOrganization,
            deleteConversionData,
            displayAddDialog,
            processingData,
            onResize
        };
    }
});
